import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { 
  User, 
  Mail, 
  Calendar, 
  Shield, 
  Lock, 
  CheckCircle, 
  XCircle,
  Loader2,
  ArrowLeft,
  Pencil
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Profile = () => {
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Password change state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordStep, setPasswordStep] = useState('request');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Email change state
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailStep, setEmailStep] = useState('request');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API_URL}/api/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Ошибка загрузки профиля');
      }
      
      const data = await response.json();
      setProfile(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Password change handlers
  const handleRequestPasswordChange = async () => {
    setPasswordError('');
    setPasswordLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/profile/password/request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Ошибка запроса');
      }
      
      setPasswordStep('confirm');
      setPasswordSuccess('Код отправлен на вашу почту');
    } catch (err) {
      setPasswordError(err.message);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleConfirmPasswordChange = async () => {
    setPasswordError('');
    
    if (!code || code.length !== 6) {
      setPasswordError('Введите 6-значный код');
      return;
    }
    
    if (!newPassword || newPassword.length < 8) {
      setPasswordError('Пароль должен содержать минимум 8 символов');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Пароли не совпадают');
      return;
    }
    
    const hasLetter = /[a-zA-Zа-яА-Я]/.test(newPassword);
    const hasDigit = /[0-9]/.test(newPassword);
    
    if (!hasLetter || !hasDigit) {
      setPasswordError('Пароль должен содержать буквы и цифры');
      return;
    }
    
    setPasswordLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/profile/password/confirm`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: code,
          new_password: newPassword
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Ошибка смены пароля');
      }
      
      setPasswordSuccess('Пароль изменён. Выполняется выход...');
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);
      
    } catch (err) {
      setPasswordError(err.message);
    } finally {
      setPasswordLoading(false);
    }
  };

  const resetPasswordDialog = () => {
    setShowPasswordDialog(false);
    setPasswordStep('request');
    setPasswordError('');
    setPasswordSuccess('');
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
  };

  // Email change handlers
  const handleRequestEmailChange = async () => {
    setEmailError('');
    
    if (!newEmail || !newEmail.includes('@')) {
      setEmailError('Введите корректный email');
      return;
    }
    
    if (newEmail.toLowerCase() === profile?.email?.toLowerCase()) {
      setEmailError('Новый email совпадает с текущим');
      return;
    }
    
    setEmailLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/profile/email/request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          new_email: newEmail
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Ошибка запроса');
      }
      
      setEmailStep('confirm');
      setEmailSuccess(`Код отправлен на ${newEmail}`);
    } catch (err) {
      setEmailError(err.message);
    } finally {
      setEmailLoading(false);
    }
  };

  const handleConfirmEmailChange = async () => {
    setEmailError('');
    
    if (!emailCode || emailCode.length !== 6) {
      setEmailError('Введите 6-значный код');
      return;
    }
    
    setEmailLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/profile/email/confirm`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: emailCode
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Ошибка смены email');
      }
      
      setEmailSuccess('Email изменён. Выполняется выход...');
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);
      
    } catch (err) {
      setEmailError(err.message);
    } finally {
      setEmailLoading(false);
    }
  };

  const resetEmailDialog = () => {
    setShowEmailDialog(false);
    setEmailStep('request');
    setEmailError('');
    setEmailSuccess('');
    setNewEmail('');
    setEmailCode('');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Профиль</h1>
          <p className="text-gray-500 mt-1">Информация о вашем аккаунте</p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* User Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Данные аккаунта
            </CardTitle>
            <CardDescription>
              Информация, указанная при регистрации
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Email */}
            <div className="space-y-2">
              <Label className="text-gray-600 flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email
              </Label>
              <div className="flex items-center gap-2">
                <Input 
                  value={profile?.email || ''} 
                  disabled 
                  className="bg-gray-50 flex-1"
                />
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => setShowEmailDialog(true)}
                  title="Сменить email"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                {profile?.email_verified ? (
                  <Badge className="bg-green-100 text-green-700 border-green-200 whitespace-nowrap">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Подтверждён
                  </Badge>
                ) : (
                  <Badge variant="destructive" className="bg-red-100 text-red-700 border-red-200 whitespace-nowrap">
                    <XCircle className="h-3 w-3 mr-1" />
                    Не подтверждён
                  </Badge>
                )}
              </div>
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label className="text-gray-600 flex items-center gap-2">
                <User className="h-4 w-4" />
                Имя
              </Label>
              <Input 
                value={profile?.name || ''} 
                disabled 
                className="bg-gray-50"
              />
            </div>

            {/* Registration Date */}
            <div className="space-y-2">
              <Label className="text-gray-600 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Дата регистрации
              </Label>
              <Input 
                value={formatDate(profile?.created_at)} 
                disabled 
                className="bg-gray-50"
              />
            </div>
          </CardContent>
        </Card>

        {/* Security Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Безопасность
            </CardTitle>
            <CardDescription>
              Управление паролем
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-start gap-3">
                <Lock className="h-5 w-5 text-gray-500 mt-0.5" />
                <div className="flex-1">
                  <p className="text-gray-700 font-medium">Пароль</p>
                  <p className="text-gray-500 text-sm mt-1">
                    Для смены пароля потребуется подтверждение через email
                  </p>
                </div>
              </div>
            </div>
            
            <Button 
              onClick={() => setShowPasswordDialog(true)}
              variant="outline"
              className="w-full"
            >
              <Lock className="mr-2 h-4 w-4" />
              Сменить пароль
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Password Change Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={resetPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {passwordStep === 'request' ? 'Смена пароля' : 'Введите код'}
            </DialogTitle>
            <DialogDescription>
              {passwordStep === 'request' 
                ? 'На вашу почту будет отправлен код подтверждения'
                : 'Введите код из письма и новый пароль'
              }
            </DialogDescription>
          </DialogHeader>

          {passwordError && (
            <Alert variant="destructive">
              <AlertDescription>{passwordError}</AlertDescription>
            </Alert>
          )}

          {passwordSuccess && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-700">{passwordSuccess}</AlertDescription>
            </Alert>
          )}

          {passwordStep === 'request' ? (
            <div className="space-y-4">
              <p className="text-gray-600 text-sm">
                Код будет отправлен на: <strong className="text-gray-900">{profile?.email}</strong>
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={resetPasswordDialog}
                  className="flex-1"
                >
                  Отмена
                </Button>
                <Button
                  onClick={handleRequestPasswordChange}
                  disabled={passwordLoading}
                  className="flex-1"
                >
                  {passwordLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Отправить код'
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Код из письма</Label>
                <Input
                  type="text"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-xl tracking-widest"
                  maxLength={6}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Новый пароль</Label>
                <Input
                  type="password"
                  placeholder="Минимум 8 символов"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Подтверждение пароля</Label>
                <Input
                  type="password"
                  placeholder="Повторите пароль"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              
              <p className="text-gray-500 text-xs">
                Пароль должен содержать минимум 8 символов, буквы и цифры
              </p>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setPasswordStep('request')}
                  className="flex-1"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Назад
                </Button>
                <Button
                  onClick={handleConfirmPasswordChange}
                  disabled={passwordLoading || !code || !newPassword}
                  className="flex-1"
                >
                  {passwordLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Сменить пароль'
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Change Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={resetEmailDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {emailStep === 'request' ? 'Смена email' : 'Подтверждение'}
            </DialogTitle>
            <DialogDescription>
              {emailStep === 'request' 
                ? 'Введите новый email. На него будет отправлен код подтверждения.'
                : 'Введите код, отправленный на новый email'
              }
            </DialogDescription>
          </DialogHeader>

          {emailError && (
            <Alert variant="destructive">
              <AlertDescription>{emailError}</AlertDescription>
            </Alert>
          )}

          {emailSuccess && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-700">{emailSuccess}</AlertDescription>
            </Alert>
          )}

          {emailStep === 'request' ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Текущий email</Label>
                <Input
                  value={profile?.email || ''}
                  disabled
                  className="bg-gray-50"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Новый email</Label>
                <Input
                  type="email"
                  placeholder="новый@email.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={resetEmailDialog}
                  className="flex-1"
                >
                  Отмена
                </Button>
                <Button
                  onClick={handleRequestEmailChange}
                  disabled={emailLoading || !newEmail}
                  className="flex-1"
                >
                  {emailLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Отправить код'
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-gray-600 text-sm">
                Код отправлен на: <strong className="text-gray-900">{newEmail}</strong>
              </p>
              
              <div className="space-y-2">
                <Label>Код подтверждения</Label>
                <Input
                  type="text"
                  placeholder="000000"
                  value={emailCode}
                  onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-xl tracking-widest"
                  maxLength={6}
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setEmailStep('request')}
                  className="flex-1"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Назад
                </Button>
                <Button
                  onClick={handleConfirmEmailChange}
                  disabled={emailLoading || !emailCode}
                  className="flex-1"
                >
                  {emailLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Подтвердить'
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Profile;
